import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { validate } from '../../../validation/engine';
import ResponseShell from './ResponseShell';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { useAutosave } from './useAutosave';
import { type ProcedureResponseProps, computeResponseReadOnly } from './responseShellTypes';
import {
  transitionService,
  procedureInstanceService,
  riskAssessmentResponseService,
  controlTestResponseService,
  controlImplResponseService,
  riskControlsService,
  findingService,
  procedureEvidenceService,
  type Finding,
  type RiskAssessmentResponseRow,
  type ControlTestResponseRow,
  type ControlImplementationRow,
  type RiskControlRow,
  type ProcedureEvidence,
} from '../../../services/auditService';
import riskAssessmentService from '../../../services/riskAssessmentService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, { ITEM_TYPE_RISK, ITEM_TYPE_CONTROL } from './RaiseFindingButton';
import InlineQuestionMeta from './InlineQuestionMeta';
import EvidenceCell from './EvidenceCell';
import RejectNoteCell from './RejectNoteCell';
import {
  SeamlessSurface,
  SeamlessCard,
  TypeChip,
  AssessmentPanel,
  DualScorePanel,
  AssessRow,
  NetScore,
  SectionLabel,
  ImplList,
  SeamlessSkeleton,
  riskBand,
  scoreBand,
} from './seamless/SeamlessKit';

/**
 * Phase 110 — combined Risk + Control assessment RAT body (CONTEXT decisions 1-4).
 *
 * Each risk attached to the procedure template (from riskAssessmentService.listScope)
 * renders as a SeamlessCard scored Likelihood × Impact (UNCHANGED from 05c-06) AND
 * expands — via the card's `impl` slot — into the full control-assessment section for
 * every control MITIGATING that risk (from GET /adt/risk_controls). Each control is a
 * nested SeamlessCard scored Existence × Effectiveness (out of 25), with its
 * implementations (Pass/Fail/NA + observations). This mirrors the compliance
 * RiskAssessmentExecutionModal Risk → Controls(Ex/Ef) → Implementations tree, reusing
 * the SeamlessKit primitives.
 *
 * Two INDEPENDENT scores (CONTEXT decision 2): the risk keeps its L×I, each control
 * keeps its Ex×Ef. NO residual/aggregation derivation mutates the risk score.
 *
 * Persistence (per-risk scoping, plan 110-01 / Option B):
 *   - Risk L×I rows → riskAssessmentResponseService (UNCHANGED).
 *   - Control Ex/Ef/result/evidence rows → controlTestResponseService carrying risk_id.
 *   - Implementation Pass/Fail/NA rows → controlImplResponseService carrying risk_id.
 *   Both control families carry risk_id so the same control scored under two risks
 *   persists as two independent rows (not a collapsed many-to-many row).
 *
 * Findings (CONTEXT decision 3 — both levels raisable, each with severity):
 *   - Risk-level vulnerability  → ObservationModal itemTypeId=ITEM_TYPE_RISK (9),
 *     itemRecordId=risk_id (NOT response_id).
 *   - Per-control deficiency    → ObservationModal itemTypeId=ITEM_TYPE_CONTROL (11),
 *     itemRecordId=control_id.
 *
 * readOnly (G3 post-approval lock) threads into every input — risk L×I, control Ex/Ef,
 * implementation results, deficiency raise, and control evidence.
 *
 * Empty-scope behaviour: no attached risks → amber banner. A risk with no mapped
 * controls → "No mitigating controls." inside its impl slot.
 */

interface RARow {
  /** Stable React key — "risk-{risk_id}". */
  key: string;
  /** RSK_register.id; FK target of risk_assessment_responses.risk_id + finding item_record_id. */
  risk_id: number;
  /** Display only. */
  risk_code?: string | null;
  /** Display only. */
  risk_name?: string | null;
  /** Display only. */
  category_name?: string | null;
  /** Editable. '' = user has not yet picked a value; treated as not-yet-savable for CREATE. */
  likelihood: number | '';
  /** Editable (Severity — stored in the `impact` column). '' = unset. */
  impact: number | '';
  /** Phase 110 — RISK-level control assessment (drives Residual). DISTINCT from the
   *  per-control Ex/Ef in the nested mitigating-controls tree. '' = unset. */
  existence_score: number | '';
  effectiveness_score: number | '';
  /** Editable. */
  rationale: string;
  /** Server-assigned after first save. */
  response_id?: number;
  /** G2 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  dirty: boolean;
}

/** A control mitigating a specific risk — scored Existence × Effectiveness, with its
 *  saved result/evidence + a per-risk response_id (control_test_responses.id). */
interface ControlRow {
  key: string;                 // "rc-{risk_id}-{control_id}"
  risk_id: number;
  control_id: number;
  control_code?: string | null;
  control_name?: string | null;
  control_type?: string | null;
  existence_score: number | '';
  effectiveness_score: number | '';
  result: 'Pass' | 'Fail' | 'NA' | '';
  evidence_text: string;
  evidence_file_id: number | null;
  evidence_filename?: string | null;
  response_id?: number;        // adt_control_test_responses.id (per-risk)
  dirty: boolean;
}

/** An implementation step under a control, scored per-risk. */
interface ImplRow {
  key: string;                 // "ri-{risk_id}-{implementation_id}"
  risk_id: number;
  control_id: number;
  implementation_id: number;
  sequence_number?: number | null;
  label: string;               // explanation (the implementation description)
  implementation_type?: string | null;
  response_id?: number | null; // adt_control_implementation_responses.id (per-risk)
  result: 'Pass' | 'Fail' | 'NA' | '';
  observations: string;
  dirty: boolean;
}

/** RemarksModal — per-row note editor as a popup (parity with ControlTest). */
const RemarksModal: React.FC<{
  heading: string;
  title: string;
  initialValue: string;
  readOnly: boolean;
  placeholder?: string;
  testId?: string;
  onCommit: (value: string) => void;
  onClose: () => void;
}> = ({ heading, title, initialValue, readOnly, placeholder, testId, onCommit, onClose }) => {
  const [draft, setDraft] = useState(initialValue);
  return (
    <Modal isOpen onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>{`Remarks under "${title}"`}</Modal.Header>
      <Modal.Body>
        <FormControl.Textarea
          label=""
          data-testid={testId}
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
          rows={5}
          placeholder={placeholder}
          autoFocus
          disabled={readOnly}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="fill" onClick={() => { onCommit(draft); onClose(); }} disabled={readOnly}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};

const RiskAssessmentResponse: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;
  // G3 — an Approved instance is immutable. Derive from workflow_state unless the
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  const [rows, setRows] = useState<RARow[]>([]);
  // Controls mitigating each risk (Ex × Ef) + their implementations (Pass/Fail/NA).
  // Flat cross-risk lists keyed by (risk_id, control_id|implementation_id).
  const [controls, setControls] = useState<ControlRow[]>([]);
  const [impls, setImpls] = useState<ImplRow[]>([]);
  const [scopeMissing, setScopeMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  // Cards are always open (no expand/collapse) — the auditor scores every risk.

  // Plan 05b-05 / STAFF-06 — inline finding raise state. The risk vulnerability
  // links to risk_id (D5); a control deficiency links to control_id.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    itemTypeId: number;
    itemRecordId: number;
  } | null>(null);
  // Rationale / evidence-note / observation popup (parity with ControlTest).
  const [remarks, setRemarks] = useState<
    { heading: string; title: string; value: string; placeholder?: string; testId?: string; onCommit: (v: string) => void } | null
  >(null);

  // Phase 113 REQ-8 — per-risk and per-control evidence, fetched once and grouped.
  const [evidenceByRisk, setEvidenceByRisk] = useState<Map<number, ProcedureEvidence[]>>(new Map());
  const [evidenceByControl, setEvidenceByControl] = useState<Map<number, ProcedureEvidence[]>>(new Map());

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal — badges just won't show. Body still works.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const riskMap = new Map<number, ProcedureEvidence[]>();
      const ctrlMap = new Map<number, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id === ITEM_TYPE_RISK && ev.item_record_id != null) {
          const bucket = riskMap.get(ev.item_record_id) ?? [];
          bucket.push(ev);
          riskMap.set(ev.item_record_id, bucket);
        } else if (ev.item_type_id === ITEM_TYPE_CONTROL && ev.item_record_id != null) {
          const bucket = ctrlMap.get(ev.item_record_id) ?? [];
          bucket.push(ev);
          ctrlMap.set(ev.item_record_id, bucket);
        }
      }
      setEvidenceByRisk(riskMap);
      setEvidenceByControl(ctrlMap);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  const findingsForRisk = useCallback(
    (riskId: number): Finding[] =>
      findings.filter(
        (f) => f.item_type_id === ITEM_TYPE_RISK && f.item_record_id === riskId,
      ),
    [findings],
  );

  const findingsForControl = useCallback(
    (controlId: number): Finding[] =>
      findings.filter(
        (f) => f.item_type_id === ITEM_TYPE_CONTROL && f.item_record_id === controlId,
      ),
    [findings],
  );

  // Initial load — fetch procedure_template_id from the instance, then the RA scope
  // (one row per attached risk), the existing risk_assessment_responses, then per risk
  // the mitigating controls (GET /adt/risk_controls) merged with saved control-test +
  // control-implementation rows (bulk-listed once, grouped by risk_id locally — no N+1
  // on the saved-rows side; one cascade call per risk for the live control linkage).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inst = await procedureInstanceService.get(instance.id);
        const templateId = inst.procedure_template_id ?? null;

        let scope: Array<{
          risk_id: number;
          risk_code?: string | null;
          risk_name?: string | null;
          category_name?: string | null;
        }> = [];
        if (templateId) {
          try {
            const items = await riskAssessmentService.listScope(templateId);
            // Defensive shape normalisation — service already returns an array, but
            // tolerate `{data: []}` envelope just in case.
            const arr: any[] = Array.isArray(items) ? items : ((items as any)?.data ?? []);
            scope = arr
              .map((row: any) => ({
                risk_id: row.risk_id ?? row.id ?? row.risk?.id,
                risk_code: row.risk_code ?? row.risk?.risk_code ?? row.code ?? null,
                risk_name: row.risk_name ?? row.risk?.risk_name ?? row.name ?? null,
                category_name: row.category_name ?? row.risk?.category_name ?? null,
              }))
              .filter((r: any) => r.risk_id != null);
          } catch {
            // Scope fetch failed — fall through to empty scope.
          }
        }

        if (scope.length === 0) {
          if (!cancelled) {
            setScopeMissing(true);
            setRows([]);
            setControls([]);
            setImpls([]);
          }
          return;
        }

        const responses: RiskAssessmentResponseRow[] =
          await riskAssessmentResponseService.list(instance.id);
        const byRiskId = new Map<number, RiskAssessmentResponseRow>();
        for (const r of responses) byRiskId.set(r.risk_id, r);

        const merged: RARow[] = scope.map((s) => {
          const existing = byRiskId.get(s.risk_id);
          const likelihood: number | '' =
            existing?.likelihood == null ? '' : existing.likelihood;
          const impact: number | '' =
            existing?.impact == null ? '' : existing.impact;
          return {
            key: `risk-${s.risk_id}`,
            risk_id: s.risk_id,
            risk_code: s.risk_code,
            risk_name: s.risk_name,
            category_name: s.category_name,
            likelihood,
            impact,
            existence_score: existing?.existence_score == null ? '' : existing.existence_score,
            effectiveness_score: existing?.effectiveness_score == null ? '' : existing.effectiveness_score,
            rationale: existing?.rationale ?? '',
            response_id: existing?.id,
            reject_note: (existing as any)?.reject_note ?? '',
            dirty: false,
          };
        });

        // --- Control sub-tree -------------------------------------------------
        // Saved control-test + implementation rows (bulk, grouped by risk locally).
        let savedCtrl: ControlTestResponseRow[] = [];
        let savedImpl: ControlImplementationRow[] = [];
        try {
          savedCtrl = await controlTestResponseService.list(instance.id);
        } catch {
          // Non-fatal — saved control scores just won't pre-fill.
        }
        try {
          savedImpl = await controlImplResponseService.list(instance.id);
        } catch {
          // Non-fatal — saved implementation results just won't pre-fill.
        }
        const ctrlByPair = new Map<string, ControlTestResponseRow>();
        for (const c of savedCtrl) {
          // Only per-risk rows participate in the RAT tree (risk_id set). Legacy CAT
          // rows (risk_id null) are ignored here.
          if (c.risk_id != null) ctrlByPair.set(`${c.risk_id}-${c.control_id}`, c);
        }
        const implByPair = new Map<string, ControlImplementationRow>();
        for (const i of savedImpl) {
          if (i.risk_id != null) implByPair.set(`${i.risk_id}-${i.implementation_id}`, i);
        }

        // Live controls-under-risk cascade, one call per risk (controls differ per risk).
        const cascades = await Promise.all(
          scope.map(async (s) => {
            try {
              const list = await riskControlsService.list(instance.id, s.risk_id);
              return { risk_id: s.risk_id, list };
            } catch {
              return { risk_id: s.risk_id, list: [] as RiskControlRow[] };
            }
          }),
        );

        const allControls: ControlRow[] = [];
        const allImpls: ImplRow[] = [];
        for (const { risk_id, list } of cascades) {
          for (const c of list) {
            const saved = ctrlByPair.get(`${risk_id}-${c.control_id}`);
            allControls.push({
              key: `rc-${risk_id}-${c.control_id}`,
              risk_id,
              control_id: c.control_id,
              control_code: c.control_code ?? null,
              control_name: c.control_name ?? null,
              control_type: c.control_type ?? null,
              existence_score: (saved?.existence_score ?? '') as number | '',
              effectiveness_score: (saved?.effectiveness_score ?? '') as number | '',
              result: (saved?.result as ControlRow['result']) ?? '',
              evidence_text: saved?.evidence_text ?? '',
              evidence_file_id: saved?.evidence_file_id ?? null,
              evidence_filename: (saved as any)?.evidence_filename ?? null,
              response_id: saved?.id,
              dirty: false,
            });
            for (const im of c.implementations ?? []) {
              const savedI = implByPair.get(`${risk_id}-${im.id}`);
              allImpls.push({
                key: `ri-${risk_id}-${im.id}`,
                risk_id,
                control_id: c.control_id,
                implementation_id: im.id,
                sequence_number: im.sequence_number ?? null,
                label: im.explanation || `Implementation ${im.id}`,
                implementation_type: im.implementation_type ?? null,
                response_id: savedI?.response_id ?? null,
                result: (savedI?.result as ImplRow['result']) ?? '',
                observations: savedI?.observations ?? '',
                dirty: false,
              });
            }
          }
        }

        if (!cancelled) {
          setRows(merged);
          setControls(allControls);
          setImpls(allImpls);
        }
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Failed to load risk-assessment scope.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id]);

  // Debounced save:
  //   * Risk L×I rows — UNCHANGED (CREATE requires both scores; server recomputes
  //     computed_score, never sent).
  //   * Control rows — Ex/Ef/result/evidence persist to control_test_responses
  //     carrying risk_id (per-risk). CREATE only when there is a score or a result.
  //   * Implementation rows — Pass/Fail/NA + observations persist to
  //     control_implementation_responses carrying risk_id. CREATE only when there is
  //     a result or an observation.
  const save = useCallback(async () => {
    // 1. Risk L×I rows.
    const dirtyRisks = rows.filter((r) => r.dirty && !obsErrors[r.key]);
    const riskUpdates: Array<{ key: string; response_id?: number }> = [];
    for (const r of dirtyRisks) {
      const rEx = r.existence_score === '' ? null : (r.existence_score as number);
      const rEf = r.effectiveness_score === '' ? null : (r.effectiveness_score as number);
      // Create once ANY of the four risk-level scores is set (inherent L/S OR the
      // risk-level control Ex/Ef that drives residual).
      const hasAny =
        typeof r.likelihood === 'number' || typeof r.impact === 'number' || rEx != null || rEf != null;
      if (r.response_id) {
        await riskAssessmentResponseService.update(r.response_id, {
          likelihood: typeof r.likelihood === 'number' ? r.likelihood : null,
          impact: typeof r.impact === 'number' ? r.impact : null,
          existence_score: rEx,
          effectiveness_score: rEf,
          rationale: r.rationale || null,
          modified_by: currentUserId,
          // computed_score deliberately omitted — server recomputes per CONTEXT D2.
        });
        riskUpdates.push({ key: r.key, response_id: r.response_id });
      } else if (hasAny) {
        const created = await riskAssessmentResponseService.create({
          entity_id: entityId,
          procedure_instance_id: instance.id,
          risk_id: r.risk_id,
          likelihood: typeof r.likelihood === 'number' ? r.likelihood : null,
          impact: typeof r.impact === 'number' ? r.impact : null,
          existence_score: rEx,
          effectiveness_score: rEf,
          rationale: r.rationale || null,
          created_by: currentUserId,
        });
        riskUpdates.push({ key: r.key, response_id: created?.id });
      }
    }

    // 2. Control Ex/Ef/result/evidence rows — carry risk_id for per-risk scoping.
    const dirtyControls = controls.filter((c) => c.dirty && !obsErrors[c.key]);
    const ctrlUpdates: Array<{ key: string; response_id?: number }> = [];
    for (const c of dirtyControls) {
      const ex = c.existence_score === '' ? null : c.existence_score;
      const ef = c.effectiveness_score === '' ? null : c.effectiveness_score;
      const hasScore = ex != null || ef != null;
      if (c.response_id) {
        await controlTestResponseService.update(c.response_id, {
          risk_id: c.risk_id,
          result: c.result === '' ? null : (c.result as 'Pass' | 'Fail' | 'NA'),
          evidence_text: c.evidence_text || null,
          evidence_file_id: c.evidence_file_id,
          existence_score: ex,
          effectiveness_score: ef,
          modified_by: currentUserId,
        });
        ctrlUpdates.push({ key: c.key, response_id: c.response_id });
      } else if (c.result !== '' || hasScore) {
        const created = await controlTestResponseService.create({
          entity_id: entityId,
          procedure_instance_id: instance.id,
          control_id: c.control_id,
          risk_id: c.risk_id,
          result: c.result === '' ? null : (c.result as 'Pass' | 'Fail' | 'NA'),
          evidence_text: c.evidence_text || null,
          evidence_file_id: c.evidence_file_id,
          existence_score: ex,
          effectiveness_score: ef,
          created_by: currentUserId,
        });
        ctrlUpdates.push({ key: c.key, response_id: created?.id });
      }
    }

    // 3. Implementation Pass/Fail/NA + observations — carry risk_id.
    const dirtyImpls = impls.filter((i) => i.dirty);
    const implUpdates: Array<{ key: string; response_id?: number | null }> = [];
    for (const i of dirtyImpls) {
      if (i.response_id) {
        await controlImplResponseService.update(i.response_id, {
          risk_id: i.risk_id,
          result: i.result === '' ? null : (i.result as 'Pass' | 'Fail' | 'NA'),
          observations: i.observations || null,
        });
        implUpdates.push({ key: i.key, response_id: i.response_id });
      } else if (i.result !== '' || i.observations.trim() !== '') {
        const created = await controlImplResponseService.create({
          procedure_instance_id: instance.id,
          control_id: i.control_id,
          implementation_id: i.implementation_id,
          risk_id: i.risk_id,
          result: i.result === '' ? null : (i.result as 'Pass' | 'Fail' | 'NA'),
          observations: i.observations || null,
        });
        implUpdates.push({ key: i.key, response_id: created?.id });
      }
    }

    if (riskUpdates.length) {
      setRows((prev) =>
        prev.map((r) => {
          const u = riskUpdates.find((x) => x.key === r.key);
          return u ? { ...r, response_id: u.response_id ?? r.response_id, dirty: false } : r;
        }),
      );
    }
    if (ctrlUpdates.length) {
      setControls((prev) =>
        prev.map((c) => {
          const u = ctrlUpdates.find((x) => x.key === c.key);
          return u ? { ...c, response_id: u.response_id ?? c.response_id, dirty: false } : c;
        }),
      );
    }
    if (implUpdates.length) {
      setImpls((prev) =>
        prev.map((i) => {
          const u = implUpdates.find((x) => x.key === i.key);
          return u ? { ...i, response_id: (u.response_id ?? i.response_id) ?? null, dirty: false } : i;
        }),
      );
    }
  }, [rows, controls, impls, obsErrors, entityId, currentUserId, instance.id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  const updateRow = (idx: number, patch: Partial<RARow>) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, dirty: true };
      return next;
    });
    markDirty();
  };

  const updateControl = (key: string, patch: Partial<ControlRow>) => {
    setControls((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch, dirty: true } : c)));
    markDirty();
  };

  const updateImpl = (key: string, patch: Partial<ImplRow>) => {
    setImpls((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch, dirty: true } : i)));
    markDirty();
  };

  const handleObsBlur = (rowKey: string, value: string) => {
    if (!value || !value.trim()) {
      setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    const result = validate(110, value);
    if (!result.valid) setObsErrors(prev => ({ ...prev, [rowKey]: result.message ?? 'Invalid text.' }));
    else setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
  };

  // G2 — number of risks a checker has rejected (drives the shell approve-gate).
  const rejectedCount = rows.filter(r => r.reject_note && r.reject_note.trim()).length;

  // Submit gate (2026-06-29) — every risk must be scored (likelihood AND impact)
  // before submit. '' = not yet scored.
  const unscored = rows.filter(r => r.likelihood === '' || r.impact === '').length;
  const incompleteReason =
    rows.length > 0 && unscored > 0
      ? `Score likelihood and impact for every risk before submitting — ${unscored} of ${rows.length} incomplete.`
      : null;

  return (
    <ResponseShell
      instance={instance}
      procedureType="RiskAssessment"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={status}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await flush();
      }}
      onSubmit={async () => {
        await flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async (comment) => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      {loadError && (
        <div className="inline-error inline-error--inline" role="alert">{loadError}</div>
      )}
      {scopeMissing && (
        <div className="warning-banner" role="status">
          No risks attached to this procedure template. Ask your DRC admin to
          attach risks via Procedure Templates &rarr; RiskAssessment editor.
        </div>
      )}
      {rows.length > 0 && (
        <SeamlessSurface className="seamless-tight">
          {rows.map((r, idx) => {
            const rowFindings = findingsForRisk(r.risk_id);
            // Two-row scoring (unset sliders count as 0):
            //   Inherent = Likelihood × Severity (out of 25).
            //   Risk-level control assessment (Ex × Ef) → effectiveness%; residual is
            //   the inherent risk that the controls do NOT offset.
            //   Residual = round(Inherent × (1 − (Ex×Ef)/25)).
            const lik = r.likelihood === '' ? 0 : (r.likelihood as number);
            const sev = r.impact === '' ? 0 : (r.impact as number);
            const inherent = lik * sev;
            const rEx = r.existence_score === '' ? 0 : (r.existence_score as number);
            const rEf = r.effectiveness_score === '' ? 0 : (r.effectiveness_score as number);
            const effPct = (rEx * rEf) / 25;            // control effectiveness, 0..1
            const defPct = 1 - effPct;                   // control deficiency
            const residual = Math.round(inherent * defPct);
            const score = inherent;                      // legacy alias (finding context)
            const riskControls = controls.filter((c) => c.risk_id === r.risk_id);
            const riskEvidence = evidenceByRisk.get(r.risk_id) ?? [];
            const riskFindings = findingsForRisk(r.risk_id);
            return (
              <SeamlessCard
                key={r.key}
                sno={idx + 1}
                snoLabel={`R${idx + 1}`}
                variant="risk"
                title={r.risk_name ?? 'Unknown risk'}
                typeChip={<TypeChip label={r.category_name} />}
                assessment={
                  /* Two independent score rows sharing one box (same width as a
                     control card): Inherent (L×S) with its verdict, then the
                     risk-level control assessment (Ex×Ef) with the Residual verdict.
                     Verdict pills are label-less + equal width — the label shows on
                     hover (NetScore.title), like the control score. */
                  <DualScorePanel
                    row1={
                      <>
                        <AssessRow
                          label="Lik"
                          labelTitle="Likelihood"
                          tone="risk"
                          value={r.likelihood}
                          disabled={readOnly}
                          onChange={(v) => updateRow(idx, { likelihood: v })}
                          testId={`SEL_risk_assessment_likelihood_${r.risk_id}`}
                        />
                        <AssessRow
                          label="Sev"
                          labelTitle="Severity"
                          tone="risk"
                          value={r.impact}
                          disabled={readOnly}
                          onChange={(v) => updateRow(idx, { impact: v })}
                          testId={`SEL_risk_assessment_severity_${r.risk_id}`}
                        />
                      </>
                    }
                    verdict1={
                      <NetScore
                        text={`${inherent}`}
                        variant={riskBand(inherent)}
                        title={`Inherent risk = Likelihood × Severity = ${inherent} (out of 25)`}
                        testId={`LBL_risk_inherent_${r.risk_id}`}
                      />
                    }
                    row2={
                      <>
                        <AssessRow
                          label="Ex"
                          labelTitle="Existence (control)"
                          tone="control"
                          value={r.existence_score}
                          disabled={readOnly}
                          onChange={(v) => updateRow(idx, { existence_score: v })}
                          testId={`SEL_risk_existence_${r.risk_id}`}
                        />
                        <AssessRow
                          label="Ef"
                          labelTitle="Effectiveness (control)"
                          tone="control"
                          value={r.effectiveness_score}
                          disabled={readOnly}
                          onChange={(v) => updateRow(idx, { effectiveness_score: v })}
                          testId={`SEL_risk_effectiveness_${r.risk_id}`}
                        />
                      </>
                    }
                    verdict2={
                      <NetScore
                        text={`${residual}/25`}
                        variant={riskBand(residual)}
                        title={`Residual = Inherent ${inherent} × Deficiency ${Math.round(defPct * 100)}% = ${residual} (controls offset ${Math.round(effPct * 100)}%)`}
                        testId={`LBL_risk_residual_${r.risk_id}`}
                        emphasis
                      />
                    }
                  />
                }
                inlineActions={
                  <div className="response-action-cluster response-action-cluster--inline">
                    <button
                      type="button"
                      className="response-row-action"
                      aria-label="Add remark"
                      title={r.rationale ? 'Edit rationale' : 'Add rationale'}
                      data-testid={`BTN_risk_assessment_note_${r.risk_id}`}
                      onClick={() =>
                        setRemarks({
                          heading: 'Rationale',
                          title: r.risk_name ?? 'Risk',
                          value: r.rationale,
                          placeholder: 'Why this likelihood and impact? (optional)',
                          testId: `INP_risk_assessment_rationale_${r.risk_id}`,
                          onCommit: (v) => {
                            updateRow(idx, { rationale: v });
                            handleObsBlur(r.key, v);
                          },
                        })
                      }
                      disabled={readOnly}
                    >
                      <MessageSquare size={14} />
                    </button>
                    <EvidenceCell
                      scope="row"
                      entityId={entityId}
                      createdBy={currentUserId}
                      disabled={readOnly}
                      procedureInstanceId={instance.id}
                      itemTypeId={ITEM_TYPE_RISK}
                      itemRecordId={r.risk_id}
                      questionLabel={r.risk_name}
                      showCount={false}
                      onChanged={reloadEvidence}
                    />
                    <RaiseFindingButton
                      count={rowFindings.length}
                      showCount={false}
                      disabled={readOnly}
                      title="Raise a vulnerability"
                      data-testid={`BTN_risk_assessment_finding_${r.risk_id}`}
                      onClick={() => {
                        setModalRow({
                          context: {
                            procedureName: (instance as any).audit_procedure_name,
                            sampleLabel: r.risk_name ?? 'Unknown risk',
                            responseValue: r.rationale || (score > 0 ? `Score ${score}` : null),
                          },
                          itemTypeId: ITEM_TYPE_RISK,
                          itemRecordId: r.risk_id,
                        });
                      }}
                    />
                  </div>
                }
                inlineRemark={
                  <InlineQuestionMeta
                    remark={r.rationale}
                    evidence={riskEvidence}
                    findings={riskFindings}
                    readOnly={readOnly}
                    onEvidenceRemoved={reloadEvidence}
                    onRemarkClick={readOnly ? undefined : () =>
                      setRemarks({
                        heading: 'Rationale',
                        title: r.risk_name ?? 'Risk',
                        value: r.rationale,
                        placeholder: 'Why this likelihood and impact? (optional)',
                        testId: `INP_risk_assessment_rationale_${r.risk_id}`,
                        onCommit: (v) => {
                          updateRow(idx, { rationale: v });
                          handleObsBlur(r.key, v);
                        },
                      })
                    }
                  />
                }
                impl={
                  <>
                    {riskControls.length === 0 && (
                      <p className="seamless__impl-sub" style={{ padding: '8px 0' }}>No mitigating controls.</p>
                    )}
                    {riskControls.map((c, ci) => {
                      const ctrlFindings = findingsForControl(c.control_id);
                      const ctrlEvidence = evidenceByControl.get(c.control_id) ?? [];
                      const net =
                        (c.existence_score === '' ? 0 : (c.existence_score as number)) *
                        (c.effectiveness_score === '' ? 0 : (c.effectiveness_score as number));
                      const cVariant = scoreBand(net);
                      const implsForControl = impls.filter(
                        (i) => i.risk_id === r.risk_id && i.control_id === c.control_id,
                      );
                      return (
                        <SeamlessCard
                          key={c.key}
                          sno={ci + 1}
                          snoLabel={`C${ci + 1}`}
                          variant="control"
                          title={c.control_name ?? 'Unknown control'}
                          typeChip={<TypeChip label={c.control_type} />}
                          assessment={
                            <AssessmentPanel
                              verdict={
                                <NetScore
                                  text={`${net}/25`}
                                  variant={cVariant}
                                  title="Net Score = Existence × Effectiveness (out of 25)"
                                  testId={`LBL_rat_control_net_score_${r.risk_id}_${c.control_id}`}
                                />
                              }
                            >
                              <AssessRow
                                label="Ex"
                                labelTitle="Existence"
                                tone="control"
                                value={c.existence_score}
                                disabled={readOnly}
                                onChange={(v) => updateControl(c.key, { existence_score: v })}
                                testId={`SEL_rat_control_existence_${r.risk_id}_${c.control_id}`}
                              />
                              <AssessRow
                                label="Ef"
                                labelTitle="Effectiveness"
                                tone="control"
                                value={c.effectiveness_score}
                                disabled={readOnly}
                                onChange={(v) => updateControl(c.key, { effectiveness_score: v })}
                                testId={`SEL_rat_control_effectiveness_${r.risk_id}_${c.control_id}`}
                              />
                            </AssessmentPanel>
                          }
                          inlineRemark={
                            <InlineQuestionMeta
                              remark={c.evidence_text}
                              evidence={ctrlEvidence}
                              findings={ctrlFindings}
                              readOnly={readOnly}
                              onEvidenceRemoved={reloadEvidence}
                              onRemarkClick={readOnly ? undefined : () =>
                                setRemarks({
                                  heading: 'Evidence note',
                                  title: c.control_name ?? 'Control',
                                  value: c.evidence_text,
                                  placeholder: 'Note the evidence you reviewed (optional)',
                                  testId: `INP_rat_control_evidence_text_${c.key}`,
                                  onCommit: (v) => {
                                    setObsErrors((prev) => { const n = { ...prev }; delete n[c.key]; return n; });
                                    updateControl(c.key, { evidence_text: v });
                                    handleObsBlur(c.key, v);
                                  },
                                })
                              }
                            />
                          }
                          impl={
                            implsForControl.length > 0 ? (
                              <>
                                <SectionLabel>Implementations</SectionLabel>
                                <ImplList
                                  items={implsForControl.map((im) => ({
                                    id: im.implementation_id,
                                    sno: im.sequence_number,
                                    title: im.label,
                                    sub: im.implementation_type,
                                    result: im.result,
                                    observations: im.observations,
                                  }))}
                                  onResult={(id, result) =>
                                    updateImpl(`ri-${r.risk_id}-${id}`, { result: result as ImplRow['result'] })
                                  }
                                  onObservations={(id, text) =>
                                    updateImpl(`ri-${r.risk_id}-${id}`, { observations: text })
                                  }
                                  onOpenObservation={(id) => {
                                    const im = implsForControl.find((x) => x.implementation_id === id);
                                    setRemarks({
                                      heading: 'Observation',
                                      title: im?.label ?? 'Implementation',
                                      value: im?.observations ?? '',
                                      placeholder: 'Observation (optional)',
                                      testId: `INP_rat_control_impl_obs_${r.risk_id}_${id}`,
                                      onCommit: (v) => updateImpl(`ri-${r.risk_id}-${id}`, { observations: v }),
                                    });
                                  }}
                                  disabled={readOnly}
                                  testIdPrefix={`rat_control_impl_${r.risk_id}`}
                                />
                              </>
                            ) : undefined
                          }
                          inlineActions={
                            <div className="response-action-cluster response-action-cluster--inline">
                              <button
                                type="button"
                                className="response-row-action"
                                aria-label="Add remark"
                                title={c.evidence_text ? 'Edit evidence note' : 'Add evidence note'}
                                data-testid={`BTN_rat_control_note_${r.risk_id}_${c.control_id}`}
                                onClick={() =>
                                  setRemarks({
                                    heading: 'Evidence note',
                                    title: c.control_name ?? 'Control',
                                    value: c.evidence_text,
                                    placeholder: 'Note the evidence you reviewed (optional)',
                                    testId: `INP_rat_control_evidence_text_${c.key}`,
                                    onCommit: (v) => {
                                      setObsErrors((prev) => { const n = { ...prev }; delete n[c.key]; return n; });
                                      updateControl(c.key, { evidence_text: v });
                                      handleObsBlur(c.key, v);
                                    },
                                  })
                                }
                                disabled={readOnly}
                              >
                                <MessageSquare size={14} />
                              </button>
                              <EvidenceCell
                                scope="row"
                                entityId={entityId}
                                createdBy={currentUserId}
                                disabled={readOnly}
                                procedureInstanceId={instance.id}
                                itemTypeId={ITEM_TYPE_CONTROL}
                                itemRecordId={c.control_id}
                                questionLabel={c.control_name}
                                showCount={false}
                                onChanged={reloadEvidence}
                              />
                              <RaiseFindingButton
                                count={ctrlFindings.length}
                                showCount={false}
                                disabled={readOnly}
                                title="Raise a deficiency"
                                data-testid={`BTN_rat_control_finding_${r.risk_id}_${c.control_id}`}
                                onClick={() => {
                                  setModalRow({
                                    context: {
                                      procedureName: (instance as any).audit_procedure_name,
                                      sampleLabel: c.control_name ?? 'Unknown control',
                                      responseValue: c.evidence_text || (net > 0 ? `Score ${net}/25` : null),
                                    },
                                    itemTypeId: ITEM_TYPE_CONTROL,
                                    itemRecordId: c.control_id,
                                  });
                                }}
                              />
                            </div>
                          }
                        />
                      );
                    })}
                  </>
                }
                footer={
                  <RejectNoteCell
                    responseId={r.response_id}
                    rejectNote={r.reject_note}
                    procedureType="RiskAssessment"
                    role={role}
                    readOnly={readOnly}
                    onSaved={(note) =>
                      setRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, reject_note: note } : x)))
                    }
                  />
                }
              />
            );
          })}
        </SeamlessSurface>
      )}
      {rows.length === 0 && !loadError && !scopeMissing && <SeamlessSkeleton />}

      {/* Inline finding raise — polymorphic link is risk_id (vulnerability,
          ITEM_TYPE_RISK) OR control_id (deficiency, ITEM_TYPE_CONTROL). */}
      <ObservationModal
        data-testid="MOD_risk_assessment_observation"
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: (instance as any).audit_procedure_name }}
        itemTypeId={modalRow?.itemTypeId ?? ITEM_TYPE_RISK}
        itemRecordId={modalRow?.itemRecordId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
      {/* Rationale / evidence-note / observation popup (parity with ControlTest). */}
      {remarks && (
        <RemarksModal
          heading={remarks.heading}
          title={remarks.title}
          initialValue={remarks.value}
          placeholder={remarks.placeholder}
          testId={remarks.testId}
          readOnly={readOnly}
          onCommit={remarks.onCommit}
          onClose={() => setRemarks(null)}
        />
      )}
    </ResponseShell>
  );
};

export default RiskAssessmentResponse;
